import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Tabs, Button, Icon, message, Modal as AntdModal } from 'antd';
import Modal from '../../../../../../components/Modal/Modal';
import BrokerageSummary from './components/BrokerageSummary';
import SubscriptionTable from './components/BrokerageSubscriptionsTable';
import PromoCodeTable from './components/PromoCodeTable';
import CreatePromoCodeForm from './components/CreatePromoCodeForm';
import AssignedUsersTable from './components/AssignedUsersTable';
import AddUsersModal from './components/AddUsersModal';
import EditOrgUserInviteeForm from './components/EditOrgUserInviteeForm';
import HelpTip from './components/HelpTip';
import { CREATE_PROMO_HELP, TAB_HELP } from './brokerageSubscriptionHelp';
import {
  revokePromoCode,
  reinstatePromoCode,
  revertUnusedPromoCodes,
  revokeSubscriptionKey,
  renewSubscription,
  reinstateSubscription,
  fetchBrokerageSubscriptions,
  importSubscriptions,
  sendOnboardingUserInvitations,
  updateOrgUserInvitee,
  deleteOrgUserInvitee,
} from '../../../Broker/BrokerActions';
import {
  fetchOrgUserInvitees,
  formatSendInvitationsFeedback,
  isInvitationJobInProgress,
  parseSendInvitationsResult,
  pollInvitationStatus,
  parseOrgUsersImportResult,
  formatOrgUsersImportFeedback,
} from '../../../../../Onboarding/onboardingApi';
import {
  getAllocatableCapacity,
  getInviteeRecordId,
  getPendingRowKey,
  isInvitationSentStatus,
  mergePendingInviteRows,
  resolveEditableAssignmentRecord,
  partitionSendActions,
  partitionSendTargets,
} from '../../../../../Onboarding/pendingInviteWorkflow';
import {
  brokerageSubscriptionsSelector,
  promoCodesSelector,
  brokerageIdSelector,
  brokerageSummarySelector,
  subscriptionsSelector,
} from '../../../Broker/BrokerSelectors';
import { MSG_ERROR } from '../../../../../../constants';
import { getApiErrorMessage } from '../../../../../../utils/httpError';
import './BrokerageSubscriptions.scss';

const { TabPane } = Tabs;

const escapeCell = (v) => {
  const s = v == null ? '' : String(v);
  return `"${s.replace(/"/g, '""')}"`;
};

const exportSubscriptionsCsv = (subscriptions) => {
  const headers = ['Company', 'Plan Name', 'Activate Date', 'Expiry Date', 'Email', 'Status'];
  const rows = subscriptions.map((s) => [
    s.organisationName || '',
    s.planName || '',
    s.activeDate || '',
    s.expiryDate || '',
    s.assignedTo || '',
    s.isRevoked ? 'Revoked' : s.status || (s.isExpired ? 'Expired' : 'Active'),
  ]);
  const csv = [headers, ...rows].map((r) => r.map(escapeCell).join(',')).join('\n');
  return { csv, filename: 'subscriptions.csv' };
};

const exportPromoCodesCsv = (promoCodes) => {
  const headers = ['Code', 'Created By', 'Purchased qty', 'Remaining', 'Promo Expiry Date', 'Status'];
  const rows = promoCodes.map((p) => [
    p.code || '',
    p.createdBy || '',
    p.quantity != null ? String(p.quantity) : '',
    p.remaining != null ? String(p.remaining) : '',
    p.expiryDate || '',
    p.isRevoked ? 'Revoked' : 'Active',
  ]);
  const csv = [headers, ...rows].map((r) => r.map(escapeCell).join(',')).join('\n');
  return { csv, filename: 'promo-codes.csv' };
};

const triggerCsvDownload = ({ csv, filename }) => {
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 800);
};

function exportBrokerageTab(activeKey, subscriptions, promoCodes) {
  if (activeKey === 'subscription') {
    if (!Array.isArray(subscriptions) || !subscriptions.length) {
      message.warning('No subscriptions to export.');
      return;
    }
    triggerCsvDownload(exportSubscriptionsCsv(subscriptions));
    message.success('Subscriptions export started — check your downloads folder.');
  } else if (activeKey === 'promoCode') {
    if (!Array.isArray(promoCodes) || !promoCodes.length) {
      message.warning('No promo codes to export.');
      return;
    }
    triggerCsvDownload(exportPromoCodesCsv(promoCodes));
    message.success('Promo codes export started — check your downloads folder.');
  }
}

const Subscription = ({
  brokerageId,
  subscriptions,
  subscriptionsRaw,
  promoCodes,
  brokerageSummary,
  revokeSubscriptionKey,
  renewSubscription,
  reinstateSubscription,
  revokePromoCode,
  reinstatePromoCode,
  revertUnusedPromoCodes,
  fetchBrokerageSubscriptions,
  importSubscriptions,
  sendOnboardingUserInvitations,
  updateOrgUserInvitee,
  deleteOrgUserInvitee,
}) => {
  const [activeTabKey, setActiveTabKey] = useState('subscription');
  const [sendingInviteKeys, setSendingInviteKeys] = useState([]);
  const [orgUserInvitees, setOrgUserInvitees] = useState([]);
  const [editingInvitee, setEditingInvitee] = useState(null);
  const [savingInvitee, setSavingInvitee] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const refreshOrgUserInvitees = useCallback(async () => {
    try {
      const response = await fetchOrgUserInvitees();
      setOrgUserInvitees(Array.isArray(response?.data) ? response.data : []);
    } catch {
      setOrgUserInvitees([]);
    }
  }, []);

  const refreshPendingData = useCallback(async () => {
    await Promise.all([fetchBrokerageSubscriptions(brokerageId), refreshOrgUserInvitees()]);
  }, [brokerageId, fetchBrokerageSubscriptions, refreshOrgUserInvitees]);

  useEffect(() => {
    refreshPendingData();
  }, [refreshPendingData]);

  const inviteCapacity = getAllocatableCapacity(brokerageSummary || subscriptionsRaw?.summary);
  const remaining = brokerageSummary ? (brokerageSummary.unAssigned ?? brokerageSummary.remaining ?? 0) : 0;

  const assignTabRows = useMemo(
    () => mergePendingInviteRows(subscriptions, orgUserInvitees),
    [subscriptions, orgUserInvitees],
  );

  const handleStaged = async (result) => {
    if (Array.isArray(result?.invitees)) {
      setOrgUserInvitees(result.invitees);
    } else {
      await refreshOrgUserInvitees();
    }
    setActiveTabKey('assign');
    message.success('User staged. Send an invite from the Subscription Onboarding tab when you are ready.');
  };

  const handleImportSuccess = async (payload, result) => {
    const parsed = result ?? parseOrgUsersImportResult(payload);
    setImportResult(parsed);
    await refreshPendingData();
    setActiveTabKey('assign');

    const feedback = formatOrgUsersImportFeedback(parsed);

    if (feedback.type === 'success') {
      message.success(feedback.text);
    } else if (feedback.type === 'warning') {
      message.warning(feedback.text);
    } else {
      message.error(feedback.text);
    }

    return parsed;
  };

  const sendInvites = async (records) => {
    if (!records.length) return { ok: false };

    const { sendable, blocked } = partitionSendTargets(records, inviteCapacity);
    if (blocked.length) {
      message.warning(
        `${blocked.length} selected user${blocked.length === 1 ? '' : 's'} could not be invited — insufficient subscription keys or unused promo codes.`,
      );
    }
    if (!sendable.length) return { ok: false };

    const { uploadIds, unsendable } = partitionSendActions(sendable);
    if (unsendable.length) {
      message.error('Some selected users cannot be invited yet. Refresh the list and try again.');
      return { ok: false };
    }

    const keys = sendable.map(getPendingRowKey).filter(Boolean);
    setSendingInviteKeys(keys);

    try {
      const response = await sendOnboardingUserInvitations({ uploadIds });
      const result = parseSendInvitationsResult(response);
      await refreshPendingData();

      const feedbackOptions = {
        requestedCount: sendable.length,
        isResend: sendable.length === 1 && isInvitationSentStatus(sendable[0]),
      };
      const feedback = formatSendInvitationsFeedback(result, feedbackOptions);
      if (feedback.type === 'success') message.success(feedback.text);
      else if (feedback.type === 'warning') message.warning(feedback.text);
      else message.error(feedback.text);

      if (isInvitationJobInProgress(result)) {
        pollInvitationStatus({ uploadIds, initial: result })
          .then((next) => {
            if (isInvitationJobInProgress(next)) return refreshPendingData();
            const nextFeedback = formatSendInvitationsFeedback(next, feedbackOptions);
            if (nextFeedback.type === 'success') message.success(nextFeedback.text);
            else if (nextFeedback.type === 'warning') message.warning(nextFeedback.text);
            else message.error(nextFeedback.text);
            return refreshPendingData();
          })
          .catch(() => undefined);
      }

      return { ok: feedback.type !== 'error', isBulk: sendable.length > 1 };
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
      return { ok: false };
    } finally {
      setSendingInviteKeys([]);
    }
  };

  const sendInvite = (record) => sendInvites([record]);

  const bulkSendInvites = (records, { requestedCount, hasCapacityLimit } = {}) => {
    if (hasCapacityLimit) {
      message.warning(
        `Available capacity can only cover ${records.length} of ${requestedCount} selected invite${requestedCount === 1 ? '' : 's'}.`,
      );
    }

    if (!records.length) return Promise.resolve({ ok: false });

    return sendInvites(records);
  };

  const handleTabChange = (key) => {
    setActiveTabKey(key);
    if (key === 'assign') {
      refreshPendingData();
    }
  };

  const handleEditAssignment = (record) => {
    const editTarget = resolveEditableAssignmentRecord(record, orgUserInvitees);
    if (!editTarget) {
      message.error('No invitee record found for this assignment.');
      return;
    }
    setEditingInvitee(editTarget);
  };

  const handleSaveInvitee = async (body) => {
    const inviteeId = getInviteeRecordId(editingInvitee);
    if (!inviteeId) {
      message.error('Invitee id is missing.');
      return;
    }

    setSavingInvitee(true);
    try {
      await updateOrgUserInvitee(inviteeId, { ...body, id: Number(inviteeId) });
      setEditingInvitee(null);
      await refreshPendingData();
      message.success('Assignment updated.');
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
      throw err;
    } finally {
      setSavingInvitee(false);
    }
  };

  const handleDeleteInvitee = async (record) => {
    const inviteeId = getInviteeRecordId(record);
    if (!inviteeId) {
      message.error('Invitee id is missing.');
      return;
    }

    try {
      await deleteOrgUserInvitee(inviteeId);
      await refreshOrgUserInvitees();
      message.success('User deleted.');
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  const addUsersAction = (
    <Modal
      title="Add Users"
      toggle={(show) => (
        <Button
          icon="plus"
          className="assign-add-users-btn"
          onClick={() => {
            setImportResult(null);
            show();
          }}
        >
          Add Users
        </Button>
      )}
    >
      {(hide) => (
        <AddUsersModal
          brokerageId={brokerageId}
          importResult={importResult}
          onStaged={async (result) => {
            await handleStaged(result);
            hide();
          }}
          importSubscriptions={importSubscriptions}
          onImportComplete={async (payload, result) => {
            await handleImportSuccess(payload, result);
            // Keep modal open so the upload summary stays visible.
          }}
        />
      )}
    </Modal>
  );

  const tabBarExtra = (
    <div className="bsub-tab-extra">
      {activeTabKey !== 'assign' && (
        <Button
          icon="export"
          size="small"
          className="bsub-export-btn"
          onClick={() => exportBrokerageTab(activeTabKey, subscriptions, promoCodes)}
        >
          Export
        </Button>
      )}
    </div>
  );

  return (
    <div className="component-wrapper">
      <Row gutter={20} type="flex">
        <Col span={24}>
          <BrokerageSummary />
        </Col>
        <Col span={24}>
          <div className="bsub-promo-bar">
            <Icon type="tags" className="bsub-promo-bar-icon" aria-hidden />
            <Modal
              title="Create promo code"
              toggle={(show) => (
                <Button className="bsub-create-promo-btn" onClick={show} disabled={remaining === 0}>
                  <Icon type="plus-circle" /> Create Promo codes
                </Button>
              )}
            >
              {(hide) => <CreatePromoCodeForm close={hide} />}
            </Modal>
            <span className="bsub-create-promo-desc">
              Click to create promo codes for a group of subscriptions.
              <HelpTip title={CREATE_PROMO_HELP} />
            </span>
          </div>

          <div className="bsub-tabs-container">
            <Tabs
              activeKey={activeTabKey}
              onChange={handleTabChange}
              tabBarExtraContent={activeTabKey === 'assign' ? null : tabBarExtra}
            >
              <TabPane
                key="subscription"
                tab={
                  <span className="bsub-tab-label">
                    Subscriptions
                    <HelpTip title={TAB_HELP.subscription} placement="bottom" />
                  </span>
                }
              >
                <SubscriptionTable
                  subscriptions={subscriptions}
                  revoke={revokeSubscriptionKey}
                  renew={renewSubscription}
                  reinstate={reinstateSubscription}
                />
              </TabPane>
              <TabPane
                key="assign"
                tab={
                  <span className="bsub-tab-label">
                    Subscription Onboarding
                    <HelpTip title={TAB_HELP.assign} placement="bottom" />
                  </span>
                }
              >
                <AssignedUsersTable
                  subscriptions={assignTabRows}
                  orgUserInvitees={orgUserInvitees}
                  availableCapacity={inviteCapacity}
                  sendingInviteKeys={sendingInviteKeys}
                  onSendInvite={sendInvite}
                  onBulkSendInvite={bulkSendInvites}
                  onEditAssignment={handleEditAssignment}
                  onDeleteInvitee={handleDeleteInvitee}
                  addUsersAction={addUsersAction}
                />
              </TabPane>
              <TabPane
                key="promoCode"
                tab={
                  <span className="bsub-tab-label">
                    Promo codes
                    <HelpTip title={TAB_HELP.promoCode} placement="bottom" />
                  </span>
                }
              >
                <PromoCodeTable
                  promoCodes={promoCodes}
                  revoke={revokePromoCode}
                  reinstate={reinstatePromoCode}
                  cancelUnused={revertUnusedPromoCodes}
                />
              </TabPane>
            </Tabs>
          </div>
        </Col>
      </Row>

      <AntdModal
        title="Edit assignment"
        visible={Boolean(editingInvitee)}
        footer={null}
        destroyOnClose
        maskClosable={false}
        onCancel={() => setEditingInvitee(null)}
      >
        {editingInvitee && (
          <EditOrgUserInviteeForm
            record={editingInvitee}
            loading={savingInvitee}
            onSave={handleSaveInvitee}
            onCancel={() => setEditingInvitee(null)}
          />
        )}
      </AntdModal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  subscriptions: brokerageSubscriptionsSelector(state),
  subscriptionsRaw: subscriptionsSelector(state),
  promoCodes: promoCodesSelector(state) || [],
  brokerageId: brokerageIdSelector(state),
  brokerageSummary: brokerageSummarySelector(state),
});

const mapDispatchToProps = {
  revokePromoCode,
  reinstatePromoCode,
  revertUnusedPromoCodes,
  revokeSubscriptionKey,
  renewSubscription,
  reinstateSubscription,
  fetchBrokerageSubscriptions,
  importSubscriptions,
  sendOnboardingUserInvitations,
  updateOrgUserInvitee,
  deleteOrgUserInvitee,
};

export default connect(mapStateToProps, mapDispatchToProps)(Subscription);
